import React from "react";
import { useNotification, useNotifications } from "../../hooks/api/useNotifications";
import ResponsiveTableWrapper from "../../components/ResponsiveTableWrapper";
import { PatternBackground } from "../../components/PatternBackground";

const getList = (data) => {
  if (Array.isArray(data)) return data;
  return data?.content || data?.items || data?.notifications || data?.results || [];
};

const getId = (item) => item?.id || item?.notificationId;
const getSubject = (item) => item?.subject || item?.title || item?.heading || "Notification";
const getMessage = (item) => item?.message || item?.body || item?.description || item?.content || "—";
const getDate = (item) => item?.createdAt || item?.createdDate || item?.sentAt || item?.timestamp || item?.date;

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};

const NotificationStatus = ({ notification }) => {
  const isRead = notification?.read === true || notification?.isRead === true || notification?.readAt;
  const label = isRead ? "Read" : "Unread";
  const color = isRead ? "#6B7280" : "#059669";

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color, fontSize: 12, fontWeight: 500 }}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: color }} />
      {label}
    </span>
  );
};

export const NotificationPage = () => {
  const [pagination, setPagination] = React.useState({ current: 1, pageSize: 10, total: 0 });
  const [selectedId, setSelectedId] = React.useState(null);
  const { data: listData, isLoading, isFetching } = useNotifications({
    page: pagination.current - 1,
    size: pagination.pageSize,
  });
  const { data: detail, isLoading: detailLoading } = useNotification(selectedId);
  const notifications = getList(listData);
  const selected = detail || notifications.find((item) => getId(item) === selectedId);
  React.useEffect(() => {
    if (listData) {
      setPagination((current) => ({
        ...current,
        total: listData?.totalElements ?? notifications.length,
      }));
    }
  }, [listData, notifications.length]);

  const handleTableChange = (nextPagination) => {
    setPagination({
      current: nextPagination.current,
      pageSize: nextPagination.pageSize,
      total: pagination.total,
    });
  };

  const columns = [
    {
      title: "Notification",
      dataIndex: "subject",
      key: "subject",
      width: 260,
      render: (_, notification) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ width: 30, height: 30, borderRadius: 8, background: "#ECFDF5", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#059669", flexShrink: 0 }}><i className="bi bi-bell" /></span>
          <strong style={{ color: "#111827" }}>{getSubject(notification)}</strong>
        </div>
      ),
    },
    {
      title: "Message",
      key: "message",
      width: 360,
      ellipsis: true,
      render: (_, notification) => getMessage(notification),
    },
    {
      title: "Date",
      key: "date",
      width: 180,
      render: (_, notification) => formatDate(getDate(notification)),
    },
    {
      title: "Status",
      key: "status",
      width: 120,
      render: (_, notification) => <NotificationStatus notification={notification} />,
    },
  ];

  if (selectedId && (selected || detailLoading)) {
    return (
      <div>
        <PatternBackground>
          <p className="lead fw-bold">Notification Details</p>
          <small>View your notification information</small>
        </PatternBackground>

        <div className="om-container" style={{ width: "97%", maxWidth: "100%", boxSizing: "border-box" }}>
          <button
            type="button"
            onClick={() => setSelectedId(null)}
            style={{ display: "flex", alignItems: "center", gap: 6, border: "1px solid #E5E7EB", borderRadius: 6, background: "#fff", padding: "8px 12px", marginBottom: 18, fontSize: 13, fontWeight: 600, color: "#059669", cursor: "pointer" }}
          >
            <i className="bi bi-arrow-left" /> Notifications
          </button>

          {detailLoading ? <div className="p-4 shadow-sm bg-white rounded" style={{ color: "#6B7280", fontSize: 13 }}>Loading notification...</div> : (
            <>
              <h1 style={{ fontSize: 24, fontWeight: 700, color: "#111827", margin: "0 0 6px" }}>{getSubject(selected)}</h1>
              <p style={{ fontSize: 13, color: "#6B7280", margin: "0 0 14px" }}>{formatDate(selected.createdAt || getDate(selected))}</p>
              <div style={{ marginBottom: 20 }}><NotificationStatus notification={selected} /></div>

              <div className="p-4 shadow-sm bg-white rounded" style={{ width: "100%", boxSizing: "border-box" }}>
                <div style={{ maxWidth: 900, marginBottom: 24 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", marginBottom: 8 }}>Message</div>
                  <div style={{ padding: "20px", background: "#fff", border: "1px solid #E5E7EB", borderRadius: 8, color: "#374151", fontSize: 14, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                    {selected.body || getMessage(selected)}
                  </div>
                </div>

              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <PatternBackground>
        <p className="lead fw-bold">Notifications</p>
        <small>Your latest notifications and updates</small>
      </PatternBackground>

      <div className="om-container" style={{ width: "97%", maxWidth: "100%", boxSizing: "border-box" }}>
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E5E7EB", padding: "4px 16px", width: "100%", boxSizing: "border-box" }}>
          <ResponsiveTableWrapper
            columns={columns}
            dataSource={notifications}
            rowKey={(notification) => getId(notification)}
            loading={isLoading || isFetching}
            onRow={(notification) => ({
              onClick: () => getId(notification) && setSelectedId(getId(notification)),
              style: { cursor: getId(notification) ? "pointer" : "default" },
            })}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} notifications`,
            }}
            locale={{ emptyText: "No notifications found" }}
            onChange={handleTableChange}
          />
        </div>
      </div>
    </div>
  );
};
